// The single lookup the body map UI renders from: one entry per BodyView, each
// carrying that view's viewBox, its non-interactive silhouette paths, and its
// tappable muscle regions (MUSCLE_BODY_MAP_PLAN.md, Task 4.1).
//
// React-free / JSX-free so scripts/validateMuscleMap.ts can import it from the
// repo root (hazard #7). JSX starts in BodyMap.tsx.

import type { BodyView } from "@medassist/shared";
import type { BodyRegion } from "./geometry.js";
import { BACK_OUTLINE, BACK_REGIONS, BACK_VIEWBOX } from "./regionsBack.js";
import { FRONT_OUTLINE, FRONT_REGIONS, FRONT_VIEWBOX } from "./regionsFront.js";
import { SIDE_OUTLINE, SIDE_REGIONS, SIDE_VIEWBOX } from "./regionsSide.js";

export type { BodyRegion } from "./geometry.js";
export { CANVAS_HEIGHT, CANVAS_WIDTH, CENTER_X, VIEWBOX } from "./geometry.js";

export interface ViewData {
  viewBox: string;
  /** Silhouette paths drawn beneath the regions; never highlighted. */
  outline: string[];
  regions: BodyRegion[];
}

export const VIEW_DATA: Record<BodyView, ViewData> = {
  front: { viewBox: FRONT_VIEWBOX, outline: FRONT_OUTLINE, regions: FRONT_REGIONS },
  back: { viewBox: BACK_VIEWBOX, outline: BACK_OUTLINE, regions: BACK_REGIONS },
  side: { viewBox: SIDE_VIEWBOX, outline: SIDE_OUTLINE, regions: SIDE_REGIONS },
};

/**
 * Maps a rendered region's IMAGE side to the subject's anatomical side
 * (TRAINER_HOUSEHOLD_AND_LATERALITY_PLAN.md D-LAT-7, hazard #1).
 * `BodyRegion.side` is "the left half of the image, not a claim about the subject's
 * side" (geometry.ts) — so the mapping inverts between the front and back views:
 *   front: you face the subject, so image-left is the subject's RIGHT.
 *   back:  the subject faces away, so image-left is the subject's LEFT.
 *   side:  one flank is drawn; there is no left/right distinction. Returns null, and
 *          the caller falls back to combined (non-lateral) shading.
 * A `center` (midline/unpaired) region likewise has no side and returns null.
 */
export function anatomicalSideForRegion(
  view: BodyView,
  regionSide: "left" | "right" | "center"
): "left" | "right" | null {
  if (view === "side" || regionSide === "center") return null;
  if (view === "front") return regionSide === "left" ? "right" : "left";
  return regionSide === "left" ? "left" : "right";
}
